import { FormHandle } from '@/hooks/use-form-handle.hook';
import { FC, useMemo, useState } from 'react';
import InputControl from '@/components/form/input-control';
import { Box } from '@mui/material';
import { TelemetryModel } from '@/lib/models/telemetry/telemetry.model';
import {
  HttpTelemetryFormatOpts,
  HttpTelemetryModel,
} from '@/lib/models/telemetry/http-telemetry.model';
import { getHttpAuthTypeOptions } from '@/lib/models/telemetry';
import Tooltip from '@/components/tooltip';
import DrawerFormSection from '@/components/form/drawer-section';

interface Props {
  telemetry: HttpTelemetryModel;
  formHandle: FormHandle<TelemetryModel>;
  isNew: boolean;
}

const HttpTelemetry: FC<Props> = ({ telemetry, formHandle, isNew }) => {
  const { setFieldValue, values } = formHandle;
  const hasAuthToken = !!telemetry?.config.authTokenLast4;
  const [updateOnly, setUpdateOnly] = useState(!isNew && hasAuthToken);

  const authTypeOptions = useMemo(() => getHttpAuthTypeOptions(), []);

  const handleAuthTypeChange = (authType: string) => {
    if (authType === 'none') {
      setFieldValue('config.authHeaderName', '');
      setFieldValue('config.authToken', '');
      setUpdateOnly(false);
      return;
    }

    setFieldValue('config.authHeaderName', authType === 'bearer' ? 'Authorization' : 'X-API-Key');

    // switching the auth type? open up the auth token field since that's likely also changing
    setUpdateOnly(!isNew && hasAuthToken);
  };

  const { usesAuthHeader, authDisabled } = values as HttpTelemetryModel;
  return (
    <Box>
      <InputControl name="config.url" label="Endpoint URL" formHandle={formHandle} />

      <DrawerFormSection label="Authentication" />

      <InputControl
        name="config.authType"
        label="Authentication Type"
        formHandle={formHandle}
        type="select"
        options={authTypeOptions}
        onChange={handleAuthTypeChange}
      />

      <Tooltip
        disabled={usesAuthHeader}
        title={`Changing the Authentication Header name is only available when Authentication Type is set to "Custom". For Bearer Authentication, this value will always be "Authorization".`}
      >
        <InputControl
          name="config.authHeaderName"
          label="Authentication Header Name"
          formHandle={formHandle}
          disabled={!usesAuthHeader}
        />
      </Tooltip>

      <Tooltip
        disabled={!authDisabled}
        title={`Setting an authentication token value is only available when Authentication Type is set to "Custom" or "Bearer".`}
      >
        <InputControl
          name="config.authToken"
          label={usesAuthHeader ? 'Custom Token' : 'Bearer Token'}
          formHandle={formHandle}
          updateOnly={updateOnly}
          disabled={authDisabled}
        />
      </Tooltip>

      <InputControl
        name="config.allowSelfSignedCertificates"
        label="Allow self-signed certificates"
        type="switch"
        formHandle={formHandle}
        inline
      />

      <DrawerFormSection label="Delivery" />

      <InputControl
        name="config.format"
        label="Format"
        type="select"
        options={HttpTelemetryFormatOpts}
        formHandle={formHandle}
        inline
      />

      <InputControl
        name="config.compress"
        label="Enable gzip compression for request body"
        type="switch"
        formHandle={formHandle}
        inline
      />
    </Box>
  );
};

export default HttpTelemetry;
